import { useContext, useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography, Switch } from '@mui/material';
import { AccountCircle, Cookie, Edit, FingerprintOutlined, Logout } from '@mui/icons-material';

import { Popup } from '@/components';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { checkCookiebot } from '@/utilities/checkCookiebot';
import { ctxUser } from '@/contexts/UserContext';
import LanguageSelect from './LanguageSelect';
import { BiometicLoginSupport } from '@/types';

import { isNotTokenUser } from '@/utilities/preconditions';
import {
  SettingsGroupTitle,
  settingsIconSx,
  SettingsPaper,
  SettingsPaperTitle,
  settingsStartIconSx,
} from './SettingsComponents.style';
import {
  CommonPageContainer,
  CommonPageHeader,
  CommonPageScrollableContent,
} from '../common/CommonPageComponents.style';

function openCookieManager() {
  Cookiebot.renew();
}

export default function ProfilePopup({ closePopup }: { closePopup: () => Promise<void> }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const ctx = useContext(ctxUser);
  const [bio, setBio] = useState<BiometicLoginSupport>(() => {
    const biometric = localStorage.getItem('biometriclogin');
    if (biometric === null) return 'false';
    if (biometric === 'notsupported') return 'notsupported';
    if (biometric === 'off') return 'off';
    return biometric === 'true' ? 'true' : 'false';
  });

  function handleBioChange(_event: ChangeEvent<HTMLInputElement>, checked: boolean) {
    setBio(checked ? 'true' : 'off');
    localStorage.setItem('biometriclogin', checked ? 'true' : 'off');
  }

  if (!ctx) return;

  const title = t('mainMenu.profile');
  const user = ctx.user;

  const body = (
    <CommonPageScrollableContent>
      {isNotTokenUser(ctx) && (
        <div>
          <SettingsGroupTitle data-qa-element-id="profile_title_label">{t('settings.profile')}</SettingsGroupTitle>
          <SettingsPaper data-qa-element-id="profile_paper">
            <AccountCircle sx={settingsStartIconSx} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography data-qa-element-id="username_label">{user.name}</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }} data-qa-element-id="email_label">
                {user.email}
              </Typography>
            </Box>
            <IconButton onClick={async () => await navigate('/logout')} data-qa-element-id="profle_logout_button">
              <Logout sx={settingsIconSx} />
            </IconButton>
          </SettingsPaper>
        </div>
      )}
      <div>
        <SettingsGroupTitle data-qa-element-id="language_title_label">{t('settings.language')}</SettingsGroupTitle>
        <LanguageSelect />
      </div>
      {bio !== 'notsupported' && isNotTokenUser(ctx) && (
        <div>
          <SettingsGroupTitle data-qa-element-id="biometrics_title_label">
            {t('settings.biometrics')}
          </SettingsGroupTitle>
          <SettingsPaper data-qa-element-id="biometrics_paper">
            <FingerprintOutlined sx={settingsStartIconSx} />

            <SettingsPaperTitle data-qa-element-id="biometrics_label">
              {t('settings.biometricAuthentication')}
            </SettingsPaperTitle>
            <Switch
              color="secondary"
              checked={bio !== 'off'}
              onChange={handleBioChange}
              data-qa-element-id="biometrics_swich_button"
            />
          </SettingsPaper>
        </div>
      )}
      {checkCookiebot() && isNotTokenUser(ctx) && (
        <div>
          <SettingsGroupTitle data-qa-element-id="cookies_title_label">{t('settings.cookies')}</SettingsGroupTitle>
          <SettingsPaper clickable={true} onClick={openCookieManager} data-qa-element-id="cookies_paper">
            <Cookie sx={settingsStartIconSx} />

            <SettingsPaperTitle data-qa-element-id="cookies_label">{t('settings.cookieManagement')}</SettingsPaperTitle>

            <Edit sx={settingsIconSx} />
          </SettingsPaper>
        </div>
      )}
    </CommonPageScrollableContent>
  );

  if (isPhoneOrHighScreen()) {
    return (
      <Popup
        popupTitle={title}
        header={undefined}
        body={body}
        footer={undefined}
        closePopup={closePopup}
        isFullScr={true}
        showCloseButton={true}
        leftText={true}
      />
    );
  }

  return (
    <CommonPageContainer data-qa-element-id="settings_view">
      <CommonPageHeader data-qa-element-id="settings_view_title">{title}</CommonPageHeader>
      {body}
    </CommonPageContainer>
  );
}
